import type {
  ApiError,
  BookingResponse,
  EarlyDeparturePlanStatus,
  OtaBookingRefundLedger,
  OtaEarlyDepartureReason,
} from '@/shared/api';
import { minorFromMajor } from '@/shared/lib/money';

export function departureDateError(
  booking: BookingResponse | undefined,
  date: string,
  mode: 'actual' | 'plan',
): string | null {
  if (!booking || !date) return 'Bitte wählen Sie ein Abreisedatum.';
  if (!booking.objectToday) return 'Der heutige Objekttag konnte nicht geladen werden.';
  if (date < booking.checkInDate) return 'Die Abreise darf nicht vor der Anreise liegen.';
  if (date >= booking.checkOutDate) {
    return 'Die Abreise muss vor dem vertraglichen Abreisetag liegen.';
  }
  if (mode === 'plan' && date <= booking.objectToday) {
    return 'Eine geplante Abreise muss nach dem heutigen Objekttag liegen.';
  }
  if (mode === 'plan' && !booking.capabilities?.canPlanOtaEarlyDeparture) {
    return 'Eine zukünftige Abreise kann für diese Buchung nicht geplant werden.';
  }
  return null;
}

export function earlyDepartureProblemText(problem: ApiError): string {
  if (problem.status === 409) {
    return 'Die Buchung wurde inzwischen geändert. Bitte laden Sie die Seite neu.';
  }
  if (problem.status === 400 || problem.fieldErrors) {
    return 'Das gewählte Abreisedatum ist für diese Buchung nicht zulässig.';
  }
  return 'Die vorzeitige Abreise konnte nicht gespeichert werden.';
}

const PLAN_LABELS: Record<EarlyDeparturePlanStatus, string> = {
  Scheduled: 'Geplant',
  Claimed: 'Wird ausgeführt',
  Completed: 'Ausgeführt',
  Cancelled: 'Aufgehoben',
  NeedsAttention: 'Klärung nötig',
};

export function earlyDeparturePlanLabel(status: EarlyDeparturePlanStatus): string {
  return PLAN_LABELS[status];
}

export function earlyDeparturePlanErrorText(code: string | null): string | null {
  if (!code) return null;
  if (code === 'ota_early_departure_version_stale') {
    return 'Die OTA-Buchung wurde geändert. Bitte prüfen und planen Sie die Abreise erneut.';
  }
  if (code === 'ota_early_departure_invalid_state') {
    return 'Der Aufenthalt kann nicht mehr automatisch vorzeitig beendet werden.';
  }
  return 'Der Plan braucht eine manuelle Prüfung.';
}

/** Ob die Eingaben für Planen, Erfassen oder Erfassen mit Mangel-Erstattung vollständig sind. */
export function earlyDepartureComplete(input: {
  booking: BookingResponse | undefined;
  mode: 'actual' | 'plan';
  reason: OtaEarlyDepartureReason;
  ledger: OtaBookingRefundLedger | undefined;
  refundAmount: string;
  refundReason: string;
}): boolean {
  const { booking, mode, reason, ledger } = input;
  if (mode === 'plan') return !!booking?.capabilities?.canPlanOtaEarlyDeparture;
  if (reason === 'Private') return !!booking?.capabilities?.canRecordOtaEarlyDeparture;
  const amountMinor = ledger ? minorFromMajor(input.refundAmount, ledger.currency) : null;
  return (
    !!ledger &&
    amountMinor !== null &&
    amountMinor > 0 &&
    amountMinor <= ledger.remainingGrossMinor &&
    input.refundReason.trim().length > 0
  );
}
